import { Buffer } from "./buffer"
import { createUniformSetters, UniformSetters, UniformTypes, UniformValue } from "./uniforms"

export class Shader<U extends UniformTypes> {
	gl: WebGL2RenderingContext
	program: WebGLProgram
	attributeLocations: Record<string, GLint> = {}
	uniformSetters: UniformSetters<U>
	vertexBuffer: Buffer

	constructor(
		gl: WebGL2RenderingContext,
		vertexSource: string,
		fragmentSource: string,
		uniforms: U,
		vertexBuffer?: Buffer,
	) {
		const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSource)
		const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource)
		const program = createProgram(gl, vertexShader, fragmentShader)

		if (program == null) {
			throw new Error("Could not initialize program")
		}

		this.gl = gl
		this.program = program
		this.uniformSetters = createUniformSetters(gl, program, uniforms) as UniformSetters<U>
		this.vertexBuffer = vertexBuffer ?? getDefaultVertexBuffer(gl)
	}

	getAttributeLocation(name: string): number {
		let location = this.attributeLocations[name]
		if (location == null) {
			location = this.gl.getAttribLocation(this.program, name)
			this.attributeLocations[name] = location
		}

		return location
	}

	use() {
		this.gl.useProgram(this.program)
	}

	set<K extends keyof U>(name: K, value: UniformValue<U, K>) {
		// TODO: fix uniform setter typing
		// @ts-expect-error `value` cannot be passed as argument has type `never`
		this.uniformSetters[name](value)
	}

	setUniforms<K extends keyof U>(uniforms: Record<K, UniformValue<U, K>>) {
		this.use()

		for (const key in uniforms) {
			// TODO: fix uniform setter typing
			// @ts-expect-error `uniforms[key]` cannot be passed as argument has type `never`
			this.uniformSetters[key](uniforms[key])
		}
	}

	initUniformBufferObject(options: {
		name: string
		buffer: WebGLBuffer
		dataSize: number
		bindingPoint?: number
	}) {
		const { name, buffer, dataSize } = options

		const blockIndex = this.gl.getUniformBlockIndex(this.program, name)
		const bindingPoint = options.bindingPoint ?? blockIndex

		this.gl.uniformBlockBinding(this.program, blockIndex, bindingPoint)
		this.gl.bindBufferBase(this.gl.UNIFORM_BUFFER, bindingPoint, buffer)

		this.gl.bindBuffer(this.gl.UNIFORM_BUFFER, buffer)
		this.gl.bufferData(this.gl.UNIFORM_BUFFER, dataSize, this.gl.DYNAMIC_DRAW)
	}

	draw(buffer = this.vertexBuffer) {
		const gl = this.gl

		gl.bindBuffer(gl.ARRAY_BUFFER, buffer.buffer)

		const positionAttributeLocation = this.getAttributeLocation("a_position")

		gl.enableVertexAttribArray(positionAttributeLocation)
		gl.vertexAttribPointer(positionAttributeLocation, buffer.size, gl.FLOAT, false, 0, 0)

		gl.drawArrays(gl.TRIANGLES, 0, buffer.count)
	}

	render<K extends keyof U>(uniforms: Record<K, UniformValue<U, K>>) {
		this.setUniforms(uniforms)
		this.draw()
	}
}

function printShaderErrors(log: string | null, source: string) {
	if (!log) {
		return
	}

	const sourceLines = source.split("\n")
	const logLines = log.split("\n")

	logLines.forEach((error) => {
		const match = error.match(/([0-9]+):([0-9]+)/) ?? []
		const line = Number(match[2])

		console.log(error)
		console.log(`${line}: ${sourceLines[line - 1]}`)
	})
}

function createShader(
	gl: WebGL2RenderingContext,
	type: GLenum,
	source: string,
): WebGLShader | null {
	const shader = gl.createShader(type)

	if (!shader) {
		return null
	}

	gl.shaderSource(shader, source)
	gl.compileShader(shader)

	const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS)

	if (success) {
		return shader
	}

	printShaderErrors(gl.getShaderInfoLog(shader), source)

	gl.deleteShader(shader)

	return null
}

function createProgram(
	gl: WebGL2RenderingContext,
	vertexShader: WebGLShader | null,
	fragmentShader: WebGLShader | null,
): WebGLProgram | null {
	if (!vertexShader || !fragmentShader) {
		return null
	}

	const program = gl.createProgram()

	if (program == null) {
		return null
	}

	gl.attachShader(program, vertexShader)
	gl.attachShader(program, fragmentShader)
	gl.linkProgram(program)

	const success = gl.getProgramParameter(program, gl.LINK_STATUS)

	if (success) {
		return program
	}

	console.error(gl.getProgramInfoLog(program))

	gl.deleteProgram(program)

	return null
}

let defaultVertexBuffer: Buffer | null = null

function getDefaultVertexBuffer(gl: WebGL2RenderingContext): Buffer {
	if (defaultVertexBuffer) {
		return defaultVertexBuffer
	}

	// prettier-ignore
	defaultVertexBuffer = new Buffer(gl, [
		-1, -1,
		-1, 1,
		1, -1,
		1, 1,
		-1, 1,
		1, -1
	], 2)

	return defaultVertexBuffer
}
